import { useCallback, useEffect, useState } from 'react'
import { api } from '../api/client.js'

export interface ChatTransfer {
  id: string
  chatId: string
  channel: string
  fromAgentId: string | null
  fromAgentName: string | null
  toAgentId: string
  toAgentName: string | null
  transferredBy: string | null
  transferredByName: string | null
  transferType: 'manual' | 'automatic'
  reason: string | null
  transferredAt: string
}

interface TransfersResponse {
  transfers: ChatTransfer[]
}

interface TransferResponse {
  transferred: boolean
  transfer: ChatTransfer
}

export interface TransferPayload {
  toAgentId: string
  reason?: string
}

export interface UseChatTransfersResult {
  transfers: ChatTransfer[]
  loading: boolean
  transferring: boolean
  error: string | null
  refetch: () => Promise<void>
  transfer: (payload: TransferPayload) => Promise<ChatTransfer | null>
}

export function useChatTransfers(chatId: string | null, channel: string | null): UseChatTransfersResult {
  const [transfers, setTransfers] = useState<ChatTransfer[]>([])
  const [loading, setLoading] = useState(false)
  const [transferring, setTransferring] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const url = chatId && channel ? `/agent/chats/${chatId}/transfers?channel=${encodeURIComponent(channel)}` : null

  const refetch = useCallback(async () => {
    if (!url) {
      setTransfers([])
      return
    }
    setLoading(true)
    setError(null)
    try {
      const res = await api.get<TransfersResponse>(url)
      setTransfers(res.transfers)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load transfer history')
      setTransfers([])
    } finally {
      setLoading(false)
    }
  }, [url])

  useEffect(() => {
    void refetch()
  }, [refetch])

  const transfer = useCallback(
    async (payload: TransferPayload): Promise<ChatTransfer | null> => {
      if (!chatId || !channel) return null
      setTransferring(true)
      setError(null)
      try {
        const res = await api.post<TransferResponse>(
          `/agent/chats/${chatId}/transfer?channel=${encodeURIComponent(channel)}`,
          payload,
        )
        if (res.transferred) {
          await refetch()
        }
        return res.transfer
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to transfer chat')
        return null
      } finally {
        setTransferring(false)
      }
    },
    [chatId, channel, refetch],
  )

  return { transfers, loading, transferring, error, refetch, transfer }
}
